import { useMemo } from "react"
import { Box, useTheme } from "@mui/material"
import _ from "lodash"

import { useTranslation } from "providers/i18n"
import { useVenue } from "providers/venue"
import { useGeoLocation } from "providers/geolocation"
import { useExtension } from "hooks/extension/useExtension"
import { EXTENSION_NAME } from "extensions/em-service-and-tourist/constant"
import { useServiceAndTourist } from "extensions/em-service-and-tourist/hooks/useServiceAndTourist"

import { getIcon } from "utils/icon"

import { EmptyData } from "components/EmptyData"
import { Head } from "components/Head"
import { ShopIcon } from "components/icons"
import { MotionDiv } from "components/motions"
import { VenueSelector } from "components/VenueSelector"
import { useVenueController } from "hooks/venue/useVenueController"
import { OccupantImageCard } from "extensions/em-core/components/ImageCard"
import { useNavigate } from "react-router-dom"

export const EmServiceAndTouristMobilePage = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const { isMultiVenueProject, isMultiOrdinalVenue, venues } = useVenue()
  const { extensionConfig } = useExtension()
  const { locationVenue } = useGeoLocation()
  const locationVenueId = _.get(locationVenue, "id")
  const { viewingVenue: venueFilter } = useVenueController(locationVenueId)

  const serviceAndTouristConfig = useMemo(
    () =>
      extensionConfig?.[EXTENSION_NAME]?.options || {
        locations: [],
      },
    [extensionConfig]
  )
  const serviceAndTouristLocations = _.get(serviceAndTouristConfig, "locations")

  const { filterByVenueId, filteredServiceAndTouristByVenue } =
    useServiceAndTourist(serviceAndTouristLocations, venueFilter)

  const theme = useTheme()
  const { styleOverrides = {} } =
    theme.components?.[`EmServiceAndTouristPage`] || {}

  const emptyIcon = useMemo(
    () => getIcon(theme?.assets?.empty?.service) || <ShopIcon />,
    [theme]
  )

  const {
    root,
    serviceItem,
    gridContainer,
    emptyDataContainer,
    venueSelector,
  } = styleOverrides

  return (
    <>
      <Head title={t("Service")} />
      <MotionDiv
        sx={root}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.15 }}
      >
        {filteredServiceAndTouristByVenue?.length > 0 ? (
          <Box sx={gridContainer}>
            {filteredServiceAndTouristByVenue?.map((data) => {
              const { id, name, image, level_name, location } = data || {}

              const serviceName = t(name)

              const levelText = isMultiOrdinalVenue
                ? t("Level Text", { level: t(level_name) })
                : ""

              return (
                <OccupantImageCard
                  sx={serviceItem}
                  key={id}
                  id={id}
                  coverImage={image}
                  primaryText={serviceName}
                  adornmentText={levelText}
                  onClick={() => navigate(`/maps/place/${location.id}`)}
                />
              )
            })}
          </Box>
        ) : (
          <Box sx={emptyDataContainer}>
            <EmptyData
              icon={emptyIcon}
              primary={t("Coming soon")}
              secondary={t("Stay tuned for the update")}
            />
          </Box>
        )}
        {isMultiVenueProject && (
          <Box sx={venueSelector}>
            <VenueSelector
              selectedVenue={venueFilter}
              venues={venues}
              onClickVenue={filterByVenueId}
              currentVenue={locationVenueId}
            />
          </Box>
        )}
      </MotionDiv>
    </>
  )
}
